// apps/web/src/components/landing-v2/FAQ.tsx

/** Common questions, semantic <details> elements. Click to expand.
 *  Honest, concise answers — no marketing fluff.
 *
 *  Items default to the landing-v2 set (platform-flavored). Pass `items`
 *  to reuse the same visual treatment with a different question set —
 *  PricingPage swaps in pricing-specific FAQ items, for example. */

export type FAQItem = { q: string; a: string }

const DEFAULT_ITEMS: FAQItem[] = [
  {
    q: 'Is this AI?',
    a: "Yes — the personalized interpretations are AI-generated. What's ours: the decks, the card teachings, and the ceremonies are created by our creators — Sarah, Adrienne, and more joining over time — and the AI personalizes those teachings to your chart and your question. For depth no AI can give you, you can book a real session with a creator.",
  },
  {
    q: 'How is this different from horoscope apps?',
    a: "You read from a creator's own deck, with the deck's own card teachings — personalized to your chart, not a generic feed. The practice is small and considered, not endless.",
  },
  { q: 'Can I cancel?', a: 'Anytime, from your account. No friction.' },
  {
    q: 'What happens after the 7-day Cosmic trial?',
    a: 'You move to $9.99/month. We send a reminder the day before; cancel anytime before then with one click.',
  },
  {
    q: 'Do you store my journal entries?',
    a: 'Stored securely and only readable by you. We never train on your entries. Delete them anytime.',
  },
  {
    q: 'Can I bring my own practice onto the platform?',
    a: 'Yes — write to hello@thecosmicfinder.com. We bring on new creators as we grow, each chosen with care.',
  },
  {
    q: 'When does a new deck or ritual drop?',
    a: "We bring on new creators and decks as we grow — each one chosen with care, on no fixed schedule. Quality over cadence.",
  },
]

type Props = { items?: FAQItem[]; heading?: string }

export function FAQ({ items = DEFAULT_ITEMS, heading = 'A few honest answers' }: Props) {
  return (
    <section id="faq" className="mx-auto max-w-[860px] px-5 py-20 md:px-8">
      <header className="reveal text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-text-muted">FAQ</p>
        <h2 className="mt-3 font-display text-4xl italic font-light md:text-5xl">{heading}</h2>
      </header>
      <ul className="mt-10 divide-y divide-hairline border-y border-hairline">
        {items.map((item, i) => (
          <li key={i} className="reveal">
            <details className="group py-5">
              <summary className="flex cursor-pointer items-center justify-between gap-4 list-none">
                <span className="font-display text-lg text-text-primary">{item.q}</span>
                <span className="text-text-muted transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm text-text-secondary body-2">{item.a}</p>
            </details>
          </li>
        ))}
      </ul>
    </section>
  )
}
